import { Slot } from "@radix-ui/react-slot"
import { clsx } from "clsx"
import {
  cloneElement,
  forwardRef,
  isValidElement,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  type Ref,
} from "react"
import { type Keys, useHotkeys } from "react-hotkeys-hook"

import type { SX } from "../../theme"
import { useButtonGroup } from "../ButtonGroup"
import { HiddenLabel } from "../HiddenLabel"
import { Icon, type IconName } from "../Icon"
import { ProgressCircle } from "../ProgressCircle"
import { Text } from "../Text"
import styles from "./Button.module.css"

export interface ButtonProps {
  children?: ReactNode
  /**
   * If true, children must be a valid ReactElement and it will be used as the
   * top level component instead of <button>.
   * Note that said component must be implemented with forwardRef() and spread/provide all its props to its first child VNode.
   */
  asChild?: boolean

  /** Note that black & white are only supported for the invisible button */
  color?:
    | "black"
    | "grey"
    | "darkGrey"
    | "purple"
    | "dark"
    | "tomato"
    | "tomato-dimmed"
    | "amber"
    | "green"
    | "white"
    | "indigo"
  size?: "small" | "medium" | "large"
  disabled?: boolean
  loading?: boolean

  textColor?: "placeholder" | "link"

  /**
   * Whether to make the text (children) content of the Button take all the available space.
   * This can be useful when the Button's width is set from the outside instead of being "auto".
   * If you set a large width from the outside but leave this prop to false/undefined,
   * the button's children will be centered, which looks strange.
   * Defaults to false.
   */
  flexContent?: boolean

  /**
   * Buttons have a bold text by default. You can override this by passing "normal".
   */
  textWeight?: "normal" | "bold"
  radius?: "normal" | "full"
  sx?: SX

  startIcon?: IconName
  endIcon?: IconName
  renderStartIcon?: (icon: ReactNode) => ReactNode
  renderEndIcon?: (icon: ReactNode) => ReactNode

  hotkeys?: Keys

  /** Invisible variant in the design system. Used for icon buttons with no background */
  invisible?: boolean
  /** Removes padding around the icon. Will only apply to icon only buttons. */
  hasPadding?: boolean
  /**  Underlines the text content of the button. Only applies to invisible buttons */
  underline?: boolean | "hover"
  hiddenLabel?: string

  /**
   * The type of HTML button to render. Defaults to "button".
   * You may want to use "submit" for your primary form button.
   */
  type?: "button" | "submit" | "reset"
  /**
   * The form ID this button is associated with.
   */
  form?: string

  onClick?: (event: MouseEvent | KeyboardEvent) => void
  onMouseDown?: (event: MouseEvent) => void
  onMouseUp?: (event: MouseEvent) => void
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const buttonGroup = useButtonGroup()

    const {
      children,
      asChild = false,
      disabled = buttonGroup?.disabled,
      startIcon,
      endIcon,
      renderStartIcon = (icon) => icon,
      renderEndIcon = (icon) => icon,
      loading = false,
      invisible = false,
      underline,
      size = buttonGroup?.size ?? (invisible ? "large" : "medium"),
      color = buttonGroup?.color ?? "purple",
      textColor,
      type = "button",
      textWeight = "bold",
      radius = "normal",
      flexContent = false,
      sx,
      hiddenLabel,
      hasPadding = true,
      hotkeys,
      onClick,
      onMouseDown,
      onMouseUp,
      ...implicitProps
    } = props

    const iconColor = getIconColor(color, invisible)
    const text = getText(children, asChild)
    const childrenProps = getChildrenProps(children, asChild)
    const iconSize = size === "small" ? "small" : "medium"
    const isDisabled = disabled || loading

    const ButtonComponent = asChild ? Slot : "button"

    const className = clsx(
      styles.button,
      styles[`size-${size}`],
      styles[`color-${color}`],
      styles[`gap-${flexContent ? 8 : 4}`],
      styles[`radius-${radius}`],
      invisible && styles.invisible,
      !hasPadding && styles.noPadding,
      loading && styles.loading,
      !text && styles.iconOnly,
      underline === true && styles.underline,
      underline === "hover" && styles.underlineOnHover,
      // disabled is not only an attribute but also a className here
      // so that the loading state doesn't look disabled, even though it physically is.
      disabled && styles.disabled,
      // pass implicit className to allow Slot to extend it
      (implicitProps as { className?: string })?.className,
    )

    useHotkeys(
      hotkeys ?? [],
      (e) => onClick?.(e),
      {
        enabled:
          !isDisabled &&
          // "react-hotkeys-hook" sometimes triggers the callback when no hotkeys are specified.
          // The following check is needed to prevent that. (See: PBD-1604, PBD-1629)
          Boolean(hotkeys?.length),
        preventDefault: true,
        enableOnFormTags: true,
        enableOnContentEditable: true,
      },
      [onClick],
    )

    function maybePreventInteraction(event: MouseEvent | React.KeyboardEvent) {
      if (!isDisabled) return

      event.preventDefault()
      event.stopPropagation()
    }

    return (
      <ButtonComponent
        {...implicitProps}
        type={type}
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        className={className}
        style={sx}
        ref={ref}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onClickCapture={maybePreventInteraction}
        onKeyDownCapture={maybePreventInteraction}
      >
        <ButtonChildren
          {...childrenProps}
          buttonChildren={children}
          asChild={asChild}
        >
          {renderStartIcon(
            startIcon && (
              <Icon name={startIcon} color={iconColor} size={iconSize} />
            ),
          )}

          {text && (
            <Text
              color={getTextColor(textColor)}
              component="span"
              variant={getTextVariant(textWeight, size)}
              noWrap
              sx={flexContent ? { flexGrow: 1 } : undefined}
            >
              {text}
            </Text>
          )}

          {renderEndIcon(
            endIcon && (
              <Icon name={endIcon} color={iconColor} size={iconSize} />
            ),
          )}

          {loading && (
            <div className={styles.loadingIndicator}>
              <ProgressCircle />
            </div>
          )}
          {hiddenLabel && <HiddenLabel>{hiddenLabel}</HiddenLabel>}
        </ButtonChildren>
      </ButtonComponent>
    )
  },
)

interface ButtonChildrenProps {
  children: ReactNode
  buttonChildren?: ReactNode
  asChild: boolean
}

interface BasicNodeWithRef {
  children?: ReactNode
  ref: Ref<Element>
}

export const ButtonChildren = forwardRef<Element, ButtonChildrenProps>(
  function ButtonChildren(props, ref) {
    const { buttonChildren, children, asChild, ...mergedProps } = props

    if (!asChild) return <>{children}</>

    // Just asserting the type, should not exit there.
    if (!isValidElement<BasicNodeWithRef>(buttonChildren))
      return <>{children}</>

    // Returning whatever element the Button user wanted but swapping its own children with the Button internal ones.
    return cloneElement(buttonChildren, { ...mergedProps, ref }, children)
  },
)

// Note: Technically, we sometimes provide an svg or a span instead of a raw text string.
function getText(children: ReactNode, asChild: boolean) {
  // Basic <Button> usage: the children are already a string or something compatible.
  if (!asChild) return children

  // Just asserting the type, should not exit there.
  if (!isValidElement<PropsWithChildren>(children)) return undefined

  // If we were passed an element to replace the default <button>, use its own text children as ours.
  return children.props.children
}

function getChildrenProps(children: ReactNode, asChild: boolean) {
  if (!asChild) return {}
  if (!isValidElement<PropsWithChildren>(children)) return {}

  return children.props
}

function getTextColor(textColor?: "placeholder" | "link") {
  if (textColor === "placeholder") return "grey11"
  if (textColor === "link") return "indigo11"
  return "inherit"
}

function getTextVariant(
  textWeight: ButtonProps["textWeight"],
  size: ButtonProps["size"],
) {
  if (textWeight === "normal") return size === "small" ? "small" : "normal"
  return size === "large" ? "emphasized" : "smallBold"
}

function getIconColor(color: ButtonProps["color"], invisible: boolean) {
  if (!invisible || !color) {
    if (color === "tomato-dimmed") return "tomato11"
    if (color === "grey") return "grey11"
    return "white"
  }

  return undefined
}
